// code-guardian-ignore-duplicates: list page and journey builder each call setJourneyActive with their own loading state and refresh; the shared part is the server-message-then-alert catch idiom of the LMS pages
import LmsDeleteConfirmModal from '../../components/LmsDeleteConfirmModal';
import LmsDeleteMenuItem from '../../components/LmsDeleteMenuItem';
import FloatingMenu from '../../components/FloatingMenu';
import { BlockLoader } from '../../components/loading';
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Plus,
  Search,
  Edit2,
  Map,
  Layers,
  Users,
  MoreVertical,
  ToggleLeft,
  ToggleRight,
  ChevronRight,
  UserPlus,
  BookOpen,
  CalendarClock,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/dateHelpers';
import { lmsAPI } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import { useLinkedSearch } from '../../hooks/useLinkedSearch';

interface JourneyStage {
  id: string;
  title: string;
  order_index: number;
  is_milestone: boolean;
}

interface LearningJourney {
  id: string;
  title: string;
  description: string | null;
  thumbnail_url: string | null;
  is_active: boolean;
  audience: 'everyone' | 'assigned';
  total_stages: number;
  content_count: number;
  /** "Complete within N days"; null = no due date. */
  due_within_days: number | null;
  stages?: JourneyStage[];
  assigned_count: number;
  completion_count: number;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
}

export default function LmsJourneysPage() {
  const navigate = useNavigate();
  const projectId = useAuthStore((state) => state.selectedProjectId) ?? undefined;
  const [journeys, setJourneys] = useState<LearningJourney[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // A link can open the list narrowed to one journey (?q=<its name>, as search does).
  const [searchQuery, setSearchQuery] = useLinkedSearch();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  // Each card's three-dots button, so its menu can be placed against the window.
  const menuAnchors = useRef<Record<string, HTMLDivElement | null>>({});
  const closeActiveDropdown = useCallback(() => setActiveDropdown(null), []);

  // Fetch journeys
  const fetchJourneys = useCallback(async () => {
    // Without a project there is nothing to fetch — but `loading` starts true,
    // so returning early here would leave the page spinning forever.
    if (!projectId) {
      setJourneys([]);
      setError('Select a project to see its learning journeys.');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const response = await lmsAPI.getJourneys({ projectId });
      setJourneys(
        (response.data?.data || []).map((j: any) => ({
          ...j,
          // Anything that is not a positive number is "no due date", so a 0 can never show on a card.
          due_within_days: Number(j.due_within_days) > 0 ? Number(j.due_within_days) : null,
        })),
      );
      setError(null);
    } catch (err) {
      console.error('Error fetching journeys:', err);
      setError('Failed to load learning journeys');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    fetchJourneys();
  }, [fetchJourneys]);

  // Filter journeys
  const filteredJourneys = journeys.filter((journey) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return journey.title.toLowerCase().includes(query) || journey.description?.toLowerCase().includes(query);
    }
    return true;
  });

  // Handle toggle active
  const handleToggleActive = async (journeyId: string, currentStatus: boolean) => {
    try {
      setActionLoading(true);
      const response = await lmsAPI.setJourneyActive(journeyId, !currentStatus, projectId);
      await fetchJourneys();
      if (response.data?.warning) alert(response.data.warning);
    } catch (err) {
      // Activation is refused for a journey with no stages or no content. That
      // message is the useful part — show it instead of a generic failure.
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update journey status';
      console.error('Error toggling journey status:', err);
      alert(message);
    } finally {
      setActionLoading(false);
      setActiveDropdown(null);
    }
  };

  // Handle delete
  const handleDelete = async (journeyId: string) => {
    try {
      setActionLoading(true);
      await lmsAPI.deleteJourney(journeyId, projectId);
      setShowDeleteConfirm(null);
      await fetchJourneys();
    } catch (err) {
      console.error('Error deleting journey:', err);
      alert('Failed to delete journey');
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Learning Journeys</h1>
          <p className="text-gray-500 mt-1">Create structured learning paths with stages and milestones</p>
        </div>
        <button
          onClick={() => navigate('/lms/journeys/new')}
          className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-primary-700"
        >
          <Plus className="w-4 h-4 inline-block mr-2" />
          Create Journey
        </button>
      </div>

      {/* Content Card */}
      <section className="rounded-lg border border-gray-100 bg-white p-6">
        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search journeys..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <BlockLoader label="Loading learning journeys..." className="py-12" />
        ) : error ? (
          <div className="text-center py-12">
            <Map className="w-8 h-8 mx-auto mb-4 text-red-500" />
            <p className="text-gray-500">{error}</p>
          </div>
        ) : filteredJourneys.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Map className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-1">No journeys yet</h3>
            <p className="text-gray-500 mb-4">
              {searchQuery
                ? 'Try adjusting your search terms.'
                : 'Create learning journeys to guide your team through structured training paths.'}
            </p>
            {!searchQuery && (
              <button
                onClick={() => navigate('/lms/journeys/new')}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700"
              >
                <Plus className="w-4 h-4" />
                Create your first journey
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredJourneys.map((journey) => (
              <div key={journey.id} className="rounded-lg border border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow">
                {/* Thumbnail */}
                <div className="aspect-video bg-gradient-to-br from-blue-100 to-indigo-100 relative overflow-hidden">
                  {journey.thumbnail_url ? (
                    <img
                      src={journey.thumbnail_url}
                      alt={journey.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Map className="w-12 h-12 text-blue-200" />
                    </div>
                  )}
                  <div className="absolute top-3 right-3">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      journey.is_active
                        ? 'bg-green-100 text-green-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {journey.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate">{journey.title}</h3>
                      {journey.description && (
                        <p className="text-sm text-gray-500 line-clamp-2 mt-1">
                          {journey.description}
                        </p>
                      )}
                    </div>

                    {/* Actions dropdown */}
                    <div className="relative" ref={(el) => { menuAnchors.current[journey.id] = el; }}>
                      <button
                        onClick={() => setActiveDropdown(activeDropdown === journey.id ? null : journey.id)}
                        aria-label="Journey actions"
                        aria-haspopup="menu"
                        aria-expanded={activeDropdown === journey.id}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </button>

                      {/* Drawn over the page, not inside the card: the card cuts off
                          whatever runs past its edge, and a short card is shorter
                          than this menu. */}
                      {activeDropdown === journey.id && (
                        <FloatingMenu role="menu" anchor={menuAnchors.current[journey.id] ?? null} onClose={closeActiveDropdown} className="w-44">
                          <button
                            onClick={() => {
                              navigate(`/lms/journeys/${journey.id}`);
                              setActiveDropdown(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                          >
                            <Edit2 className="w-4 h-4" />
                            Edit Journey
                          </button>
                          <button
                            onClick={() => {
                              navigate(`/lms/journeys/${journey.id}/assignments`);
                              setActiveDropdown(null);
                            }}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                          >
                            <UserPlus className="w-4 h-4" />
                            Assign Learners
                          </button>
                          <div className="border-t border-gray-100" />
                          <button
                            onClick={() => handleToggleActive(journey.id, journey.is_active)}
                            className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors flex items-center gap-2"
                          >
                            {journey.is_active ? (
                              <>
                                <ToggleLeft className="w-4 h-4" />
                                Deactivate
                              </>
                            ) : (
                              <>
                                <ToggleRight className="w-4 h-4" />
                                Activate
                              </>
                            )}
                          </button>
                          <div className="border-t border-gray-100" />
                          <LmsDeleteMenuItem
                            onClick={() => {
                              setShowDeleteConfirm(journey.id);
                              setActiveDropdown(null);
                            }}
                          />
                        </FloatingMenu>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mt-4 text-sm text-gray-500">
                    <div className="flex items-center gap-1">
                      <Layers className="w-4 h-4" />
                      <span>{journey.total_stages} stages</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <BookOpen className="w-4 h-4" />
                      <span>{journey.content_count ?? 0} items</span>
                    </div>
                    {journey.due_within_days && (
                      <div
                        className="flex items-center gap-1"
                        title="Counted from the day the journey is assigned to a learner, or from their joining date if they join later"
                      >
                        <CalendarClock className="w-4 h-4" />
                        <span>Due in {journey.due_within_days} {journey.due_within_days === 1 ? 'day' : 'days'}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-1">
                      <Users className={`w-4 h-4 ${journey.audience === 'assigned' ? 'text-amber-500' : ''}`} />
                      <span className={journey.audience === 'assigned' ? 'text-amber-700' : ''}>
                        {journey.assigned_count} assigned
                        {journey.audience === 'assigned' ? ' only' : ''}
                      </span>
                    </div>
                  </div>

                  {/* Stage preview */}
                  {journey.stages && journey.stages.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        {journey.stages.slice(0, 3).map((stage, index) => (
                          <span key={stage.id} className="flex items-center">
                            <span className={`px-1.5 py-0.5 rounded ${stage.is_milestone ? 'bg-yellow-100 text-yellow-700' : 'bg-gray-100'}`}>
                              {stage.title}
                            </span>
                            {index < Math.min(2, journey.stages!.length - 1) && (
                              <ChevronRight className="w-3 h-3 mx-0.5" />
                            )}
                          </span>
                        ))}
                        {journey.stages.length > 3 && (
                          <span className="text-gray-400">+{journey.stages.length - 3} more</span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Footer */}
                  <div className="mt-3 pt-3 border-t border-gray-100">
                    <p className="text-xs text-gray-400">
                      {journey.created_by_name || 'Unknown'} · {formatDate(journey.created_at)}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <LmsDeleteConfirmModal
          title="Delete Journey"
          message="Are you sure you want to delete this learning journey? This will also delete all stages and progress data. This action cannot be undone."
          onCancel={() => setShowDeleteConfirm(null)}
          onConfirm={() => handleDelete(showDeleteConfirm)}
          loading={actionLoading}
        />
      )}
    </div>
  );
}
